import { Badge, GraphStatsCircleIcon, Popover, Tooltip } from '@libs/ui/ds'
import { usePlanningTranslation } from '@libs/utils/translations'
import React from 'react'
import type { FC } from 'react'
import type { ModulationSchema } from 'snap-redux/infra/http/routesConfig/scopes/userContractCounters'

import { ModulationCounterPopoverContent } from './ModulationCounterPopoverContent'

type ModulationCountersProps = {
  modulationCounters: ModulationSchema
  contractId: number
}

export type HourFormattingFunct = (hour: number) => string

export const ModulationCounters: FC<ModulationCountersProps> = ({
  modulationCounters,
  contractId
}) => {
  const { t, tDates } = usePlanningTranslation()

  /**
   * Returns the time in h and minute
   * @example
   * ```ts
   * displayInHoursAndMinutes(1.25) => '1h25'
   * displayInHoursAndMinutes(-1.25) => '-1h25'
   * ```
   */
  const displayInHoursAndMinutes: HourFormattingFunct = hours =>
    tDates('duration.hourDecimalToHours', {
      duration: hours
    })

  const displayInHoursAndMinutesWithSign: HourFormattingFunct = hours =>
    hours > 0
      ? `+${displayInHoursAndMinutes(hours)}`
      : displayInHoursAndMinutes(hours)

  const endOfWeekBalanceBadge = (
    <Badge
      size='s'
      leftIcon={<GraphStatsCircleIcon />}
      color='neutral'
      label={displayInHoursAndMinutesWithSign(
        modulationCounters.end_of_week_balance
      )}
      data-testid={`modulation-balance-${contractId}`}
    />
  )

  if (modulationCounters.version < 2) {
    return (
      <Tooltip
        label={t('weekly.team.row.employeeCounters.modulationCounterInfo')}
      >
        {endOfWeekBalanceBadge}
      </Tooltip>
    )
  }

  return (
    <Popover trigger='hover' dropdownWidth='auto'>
      <Popover.Target>{endOfWeekBalanceBadge}</Popover.Target>
      <Popover.Dropdown>
        <ModulationCounterPopoverContent
          modulationCounters={modulationCounters}
          displayInHoursAndMinutes={displayInHoursAndMinutes}
          displayInHoursAndMinutesWithSign={displayInHoursAndMinutesWithSign}
        />
      </Popover.Dropdown>
    </Popover>
  )
}
